import { useState } from 'react';
import { api, errorMessage, type Account, type LimitCheck, type Usage } from '../api';
import type { Plan } from '../../core/plans';
import { link } from '../router';
import { daysRemaining, isNearRenewal } from '../../core/subscription';
import { MERCHANT_OF_RECORD } from '../../core/company';
import { Alert, Button, Field, Input, PageHeader, formatDate } from '../ui';
import { CheckIcon, ExternalLinkIcon, RefreshCwIcon, SparklesIcon } from '../icons';

/**
 * One metered limit, with what is left of it.
 *
 * The bar turns amber before it runs out rather than at the moment it does: a
 * customer who discovers the ceiling halfway through a questionnaire has already
 * had the bad experience.
 */
function UsageBar({ label, check }: { label: string; check: LimitCheck }) {
  const limit = check.limit;
  const pct = limit === null ? 0 : Math.min(Math.round((check.used / Math.max(limit, 1)) * 100), 100);
  const tight =
    limit !== null && check.remaining !== null && check.remaining <= Math.max(limit * 0.2, 1);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-[13px]">
        <span className="text-muted">{label}</span>
        <span className={tight ? 'text-warn' : 'text-faint'}>
          {limit === null ? 'Unlimited' : `${check.used} of ${limit}`}
        </span>
      </div>
      {limit === null ? null : (
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-sunken">
          <div
            className={`h-full transition-all ${tight ? 'bg-warn' : 'bg-accent'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function SettingsView({
  account,
  plan,
  usage,
  onUpdated,
  onPlanChanged,
}: {
  account: Account;
  plan: Plan;
  usage: Usage;
  onUpdated: (account: Account) => void;
  /** Every limit on screen depends on the plan, so a change reloads them all. */
  onPlanChanged: () => void;
}) {
  const [companyName, setCompanyName] = useState(account.companyName);
  const [maxAge, setMaxAge] = useState(account.maxEvidenceAgeDays);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [renewBusy, setRenewBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const now = Date.now();
  const subscription = {
    plan: account.plan,
    planUntil: account.planUntil,
  };
  const daysLeft = daysRemaining(subscription, now);
  const nearRenewal = isNearRenewal(subscription, now);
  const throughPolar = account.billingProvider === 'polar';

  /**
   * Invoices and the card on file live in Polar's portal, not here.
   *
   * The session is minted per click and expires on its own, so the link is not
   * something that can be bookmarked, shared, or left working in an old inbox.
   */
  const openPortal = async () => {
    setPortalBusy(true);
    setError(null);
    try {
      const { url } = await api.billingPortal();
      window.location.href = url;
    } catch (err) {
      setError(errorMessage(err));
      setPortalBusy(false);
    }
  };

  const setAutoRenew = async (autoRenew: boolean) => {
    setRenewBusy(true);
    setError(null);
    try {
      const { account: updated } = await api.setAutoRenew(autoRenew);
      onUpdated(updated);
      onPlanChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRenewBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const { account: updated } = await api.updateSettings({
        companyName,
        maxEvidenceAgeDays: maxAge,
      });
      onUpdated(updated);
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Settings" description={account.email} />

      {error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <div className="space-y-4">
        <section className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-[15px] font-medium">
                {plan.name} plan
                {plan.price > 0 ? (
                  <span className="ml-2 text-[13px] font-normal text-muted">
                    ${plan.price}/month
                  </span>
                ) : null}
              </h2>
              <p className="mt-1 text-[13px] text-muted">
                Usage resets on the first of the month. Counters shown for {usage.period}.
              </p>
            </div>
            <a {...link('/pricing')}>
              <Button icon={SparklesIcon}>See plans</Button>
            </a>
          </div>

          <div className="mt-5 space-y-4">
            <UsageBar label="Evidence cards" check={usage.evidenceCards} />
            <UsageBar label="Questionnaires this month" check={usage.questionnairesPerMonth} />
            <UsageBar label="Drafted answers this month" check={usage.draftedAnswersPerMonth} />
            <UsageBar label="Trust page documents" check={usage.trustDocuments} />
          </div>

          {account.planUntil !== null ? (
            <div className="mt-5 border-t border-line pt-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="text-[13px]">
                  {account.planAutoRenew ? (
                    <>
                      <span className={nearRenewal ? 'text-warn' : 'text-muted'}>
                        Renews on {formatDate(account.planUntil)}
                      </span>
                      <p className="mt-1 text-faint">
                        {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left in this period. Cancelling
                        keeps the plan until then.
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="text-warn">Ends on {formatDate(account.planUntil)}</span>
                      <p className="mt-1 text-faint">
                        Auto-renewal is off. The account moves to Free that day; nothing is deleted.
                      </p>
                    </>
                  )}
                </div>

                <Button
                  icon={account.planAutoRenew ? undefined : RefreshCwIcon}
                  busy={renewBusy}
                  onClick={() => void setAutoRenew(!account.planAutoRenew)}
                >
                  {account.planAutoRenew ? 'Cancel renewal' : 'Turn renewal back on'}
                </Button>
              </div>

              {throughPolar ? (
                <p className="mt-3 text-[12.5px] leading-relaxed text-faint">
                  Cancelling here tells {MERCHANT_OF_RECORD.name} to stop the renewal. You keep the
                  plan until {formatDate(account.planUntil)} either way, and nothing is deleted when
                  it ends.
                </p>
              ) : null}
            </div>
          ) : null}
        </section>

        {throughPolar ? (
          <section className="card p-5">
            <h2 className="text-[15px] font-medium">Invoices and payment method</h2>
            <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-muted">
              {MERCHANT_OF_RECORD.name} is our {MERCHANT_OF_RECORD.role} — they hold the card and
              issue the invoice, which is why none of it is stored here. Their portal has every
              invoice on the account and can reissue one with your VAT or tax number on it.
            </p>
            <Button
              className="mt-4"
              icon={ExternalLinkIcon}
              busy={portalBusy}
              onClick={() => void openPortal()}
            >
              Open billing portal
            </Button>
          </section>
        ) : null}

        <section className="card p-5">
          <h2 className="text-[15px] font-medium">Company</h2>
          <div className="mt-4">
            <Field label="Company name">
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            </Field>
          </div>
        </section>

        <section className="card p-5">
          <h2 className="text-[15px] font-medium">Freshness</h2>
          <p className="mt-1 text-[13px] text-muted">
            How long a card stays citable after its last human review. Past this, answers that rest
            on it are refused rather than warned about — a warning is something people click past.
          </p>
          <div className="mt-4 max-w-xs">
            <Field label="Review window (days)" hint="Between 30 and 1825.">
              <Input
                type="number"
                min={30}
                max={1825}
                value={maxAge}
                onChange={(e) => setMaxAge(Number(e.target.value))}
              />
            </Field>
          </div>
        </section>

        <section className="card p-5">
          <h2 className="text-[15px] font-medium">Public trust page</h2>
          <p className="mt-1 max-w-xl text-[13px] text-muted">
            The address, introduction, documents and terms now live on their own screen, because
            they are one decision rather than four settings.
          </p>
          <a {...link('/trust-page')} className="mt-4 inline-block">
            <Button icon={ExternalLinkIcon}>Open trust page settings</Button>
          </a>
        </section>

        <div className="flex items-center gap-3">
          <Button variant="primary" icon={CheckIcon} busy={busy} onClick={save}>
            Save changes
          </Button>
          {saved ? <span className="text-[13px] text-accent">Saved.</span> : null}
        </div>
      </div>
    </>
  );
}
